/**
 * Maps Cache Utility
 * Provides caching for Google Maps instances, markers, and API calls to reduce costs
 */

class MapsCache {
  constructor() {
    this.mapInstances = new Map();
    this.markers = new Map();
    this.apiCalls = new Map();
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
    this.maxCacheSize = 10; // Maximum number of cached map instances
  }

  /**
   * Generate a cache key for map instances
   */
  generateMapKey(center, zoom, venues) {
    const venueIds = venues.map(v => v.id).sort().join(',');
    return `${center.lat}_${center.lng}_${zoom}_${venueIds}`;
  }

  /**
   * Check if a map instance exists in cache
   */
  hasMapInstance(key) {
    const cached = this.mapInstances.get(key);
    if (!cached) return false;
    
    // Check if cache has expired
    if (Date.now() - cached.timestamp > this.cacheExpiry) {
      this.mapInstances.delete(key);
      return false;
    }
    
    return true;
  }

  /**
   * Get cached map configuration
   */
  getMapInstance(key) {
    const cached = this.mapInstances.get(key);
    if (cached && Date.now() - cached.timestamp <= this.cacheExpiry) {
      return cached;
    }
    return null;
  }

  /**
   * Cache map configuration (not the actual instance)
   */
  setMapInstance(key, mapInstance, markers = []) {
    // Clean up old cache if we exceed max size
    if (this.mapInstances.size >= this.maxCacheSize) {
      const oldestKey = this.mapInstances.keys().next().value;
      this.mapInstances.delete(oldestKey);
      this.markers.delete(oldestKey);
    }

    // Store map configuration instead of the instance
    this.mapInstances.set(key, {
      center: mapInstance.getCenter(),
      zoom: mapInstance.getZoom(),
      timestamp: Date.now()
    });

    if (markers.length > 0) {
      this.markers.set(key, markers);
    }
  }

  /**
   * Get cached markers
   */
  getMarkers(key) {
    const markers = this.markers.get(key);
    return Array.isArray(markers) ? markers : [];
  }

  /**
   * Clear specific map cache
   */
  clearMapCache(key) {
    const cached = this.mapInstances.get(key);
    if (cached) {
      // Clear markers from map if they exist
      const markers = this.markers.get(key) || [];
      markers.forEach(marker => {
        if (marker && marker.setMap) {
          marker.setMap(null);
        }
      });
      
      this.mapInstances.delete(key);
      this.markers.delete(key);
    }
  }

  /**
   * Clear all cache
   */
  clearAllCache() {
    // Clear all markers from maps
    this.markers.forEach(markers => {
      markers.forEach(marker => {
        if (marker && marker.setMap) {
          marker.setMap(null);
        }
      });
    });
    
    this.mapInstances.clear();
    this.markers.clear();
    this.apiCalls.clear();
  }

  /**
   * Track API calls to monitor usage
   */
  trackApiCall(endpoint, params) {
    const callKey = `${endpoint}_${JSON.stringify(params)}`;
    const now = Date.now();
    
    if (!this.apiCalls.has(callKey)) {
      this.apiCalls.set(callKey, []);
    }
    
    const calls = this.apiCalls.get(callKey);
    calls.push(now);
    
    // Keep only last 100 calls per endpoint
    if (calls.length > 100) {
      calls.splice(0, calls.length - 100);
    }
  }

  /**
   * Get API call statistics
   */
  getApiStats() {
    const stats = {};
    this.apiCalls.forEach((calls, key) => {
      const [endpoint] = key.split('_');
      if (!stats[endpoint]) {
        stats[endpoint] = 0;
      }
      stats[endpoint] += calls.length;
    });
    return stats;
  }

  /**
   * Check if Google Maps API is loaded
   */
  isGoogleMapsLoaded() {
    return !!(window.google && window.google.maps);
  }

  /**
   * Wait for Google Maps API to load
   */
  waitForGoogleMaps(timeout = 10000) {
    return new Promise((resolve, reject) => {
      if (this.isGoogleMapsLoaded()) {
        resolve();
        return;
      }

      // Check if API key is available before attempting to load
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyBbFKw4YT2iIg1uPrTcX04ADwQvlwBtbM0';
      if (!apiKey || apiKey.trim() === '') {
        reject(new Error('Google Maps API key is not configured. Please set REACT_APP_GOOGLE_MAPS_API_KEY in your .env file.'));
        return;
      }

      // Try to load Google Maps API if not already loaded
      this.loadGoogleMapsAPI();

      const startTime = Date.now();
      const checkInterval = setInterval(() => {
        if (this.isGoogleMapsLoaded()) {
          clearInterval(checkInterval);
          resolve();
        } else if (Date.now() - startTime > timeout) {
          clearInterval(checkInterval);
          reject(new Error('Google Maps API failed to load within timeout. Please check your internet connection and API key.'));
        }
      }, 100);
    });
  }

  /**
   * Load Google Maps API if not already loaded
   */
  loadGoogleMapsAPI() {
    // Check if script is already being loaded or loaded
    if (document.querySelector('script[src*="maps.googleapis.com"]') || this.isGoogleMapsLoaded()) {
      return;
    }

    // Check if API key is available
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyBbFKw4YT2iIg1uPrTcX04ADwQvlwBtbM0';
    if (!apiKey || apiKey.trim() === '') {
      // Only log warning once
      if (!this._apiKeyWarningLogged) {
        this._apiKeyWarningLogged = true;
      }
      return;
    }

    try {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
      };
      script.onerror = (error) => {
      };

      // Safely append script to head
      if (document.head) {
        document.head.appendChild(script);
      } else {
      }
    } catch (error) {
    }
  }
}

// Create singleton instance
const mapsCache = new MapsCache();

export default mapsCache;
